import type { Referidor, Referido } from '@/types';
import { tokenManager } from '@/services/auth';
import { indexedDBService } from '@/services/database';
import { GIFT_MAP } from '@/constants';

const SHEETS_API_BASE = 'https://sheets.googleapis.com/v4/spreadsheets';
export const _DRIVE_API_BASE = 'https://www.googleapis.com/drive/v3/files';

// Template spreadsheet ID from env
export const _TEMPLATE_ID = import.meta.env.VITE_TEMPLATE_SPREADSHEET_ID || '';

// Director email — gets read-only access to every new vendor spreadsheet
export const _DIRECTOR_EMAIL = import.meta.env.VITE_DIRECTOR_EMAIL || '';

// Estado inicial de la cita (columna S) con el que nace cada referido nuevo.
// Por defecto "pendiente" (falta llamarlo). Pero si el referido YA tuvo una
// cita instantanea en el campo (columna Q = CITA_INSTANTANEA), nace directo
// como "AGENDO" para que el bot no lo llame de mas.
// (Ambos valores deben coincidir EXACTO con las opciones del menu de la hoja.)
const CITA_INICIAL = 'pendiente';
const CITA_YA_AGENDADA = 'AGENDO';

export interface HistoryReferido {
  id: string;
  nombre: string;
  apellido: string;
  telefono: string;
  ciudad: string;
  relacion: string;
  trabaja: string;
  dato_extra: string;
  cita: string;       // S: estado de la cita (AGENDO, NO CONTESTA, CANCELO, ...)
  resultado: string;  // T: resultado de la visita (VENTA, NO VENTA, INTERESADA, NO HOME)
  monto: string;      // U: monto de la venta (si hubo)
}

export interface HistoryGroup {
  id: string;
  fecha: string;
  fechaOrden: number;
  referidor_nombre: string;
  referidor_telefono: string;
  regalo: string;
  venta_monto: number;   // A: monto de la venta al cliente (referidor), 0 si no hay
  referidos: HistoryReferido[];
}

export interface DuplicatePhoneInfo {
  phone: string;   // the phone the user entered that already exists
  nombre: string;  // who it's already registered under (description)
}

export interface PeriodCounts {
  today: number;
  week: number;
  month: number;
}

export interface DashboardStats {
  referidos: PeriodCounts;
  completos: PeriodCounts;
  incompletos: PeriodCounts;
  citas: PeriodCounts;
  ventas: PeriodCounts;       // numero de ventas a clientes (programas con monto)
  montoVentas: PeriodCounts;  // monto total de esas ventas ($)
  spreadsheetId: string;
}

class SheetsService {
  /**
   * Get or create the vendor's spreadsheet.
   */
  async getOrCreateVendorSheet(vendorId: string, vendorName: string): Promise<string> {
    const APPS_SCRIPT_URL = import.meta.env.VITE_GOOGLE_APPS_SCRIPT_URL || '';

    if (!APPS_SCRIPT_URL) {
      throw new Error('VITE_GOOGLE_APPS_SCRIPT_URL no configurado');
    }

    const response = await fetch(APPS_SCRIPT_URL, {
      method: 'POST',
      headers: { 'Content-Type': 'text/plain;charset=utf-8' },
      body: JSON.stringify({
        action: 'createSpreadsheet',
        payload: {
          referidor: {},
          vendedor: { email: vendorId, nombre: vendorName },
        },
      }),
      redirect: 'follow',
    });

    if (!response.ok) {
      throw new Error('Apps Script respondió ' + response.status);
    }

    const result = await response.json();
    if (!result.success || !result.data?.spreadsheetId) {
      throw new Error('Apps Script: ' + (result.error || 'sin spreadsheetId'));
    }

    const spreadsheetId = result.data.spreadsheetId;
    console.log('[Sheets] Got sheet from Registro Maestro:', spreadsheetId);
    return spreadsheetId;
  }

  /**
   * Append referidor with all their referidos
   */
  async appendReferidorWithReferidos(
    referidor: Referidor,
    referidos: Referido[]
  ): Promise<void> {
    const accessToken = await tokenManager.ensureValidToken();
    if (!accessToken) {
      throw new Error('No hay token de acceso válido');
    }

    const spreadsheetId = await this.getOrCreateVendorSheet(
      referidor.vendedor_id,
      referidor.vendedor_nombre
    );

    const rows = this.mapToSheetRows(referidor, referidos);
    await this.writeRows(spreadsheetId, accessToken, rows);
  }

  /**
   * Append a single referido (for adding more referidos to an existing referidor)
   *
   * OJO: metodo VIEJO/con bug. Escribe la fila SIN fecha (B) ni datos del
   * referidor (D/E), por lo que el referido queda huerfano en el Historial y
   * la fila (B vacia) corre riesgo de ser sobreescrita. NO usar para "agregar
   * mas referidos": para eso esta appendReferidosToProgram (abajo).
   */
  async appendReferido(referido: Referido): Promise<void> {
    const accessToken = await tokenManager.ensureValidToken();
    if (!accessToken) {
      throw new Error('No hay token de acceso válido');
    }

    const settings = await indexedDBService.getSettings(referido.vendedor_id);
    if (!settings?.spreadsheetId) {
      throw new Error('No se encontró el spreadsheet del vendedor');
    }

    const row = this.mapReferidoToRow(referido);
    await this.writeRows(settings.spreadsheetId, accessToken, [row]);
  }

  /**
   * Agrega referidos NUEVOS a un programa YA EXISTENTE (mismo referidor).
   *
   * Caso de uso: el referidor ya firmo y se guardo su programa; mas tarde (o al
   * dia siguiente) recuerda 2-4 personas mas y el asesor quiere sumarlas al
   * MISMO programa, no crear uno aparte.
   *
   * Para que agrupen junto con el programa original en el Historial, cada fila
   * nueva REPITE los datos que forman la clave de agrupacion (D + E + B):
   *   B = fecha del programa original   (group.fecha)
   *   C = asesor                        (vendorName)
   *   D = nombre del referidor          (group.referidor_nombre)
   *   E = telefono del referidor        (group.referidor_telefono)
   * F, G, H quedan VACIAS a proposito: el grupo ya las tiene de las filas
   * originales y el regalo se muestra desde la primera fila del grupo.
   *
   * La hoja SIEMPRE se resuelve por el correo del asesor via Registro Maestro
   * (getOrCreateVendorSheet), igual que el resto de la app. Nunca se usa el
   * spreadsheetId guardado por-dispositivo en IndexedDB.
   *
   * Trade-off aceptado: estos referidos cuentan en la FECHA del programa
   * original, no en el dia que se agregan.
   */
  async appendReferidosToProgram(
    group: HistoryGroup,
    vendorId: string,
    vendorName: string,
    nuevosReferidos: Referido[]
  ): Promise<void> {
    if (nuevosReferidos.length === 0) return;

    const accessToken = await tokenManager.ensureValidToken();
    if (!accessToken) {
      throw new Error('No hay token de acceso válido');
    }

    const spreadsheetId = await this.getOrCreateVendorSheet(vendorId, vendorName);

    const rows = nuevosReferidos.map((referido) =>
      this.mapReferidoToProgramRow(group, vendorName, referido)
    );

    await this.writeRows(spreadsheetId, accessToken, rows);
  }

  /**
   * Fija (o actualiza) el MONTO de la venta al cliente de un programa existente,
   * escribiendolo en la columna A de la PRIMERA fila de ese programa.
   *
   * Sirve para el caso "el cliente refiere primero y decide comprar despues":
   * desde el Historial se abre el programa y se le pone el monto cuando la venta
   * se concreta. Si monto <= 0, limpia la celda.
   *
   * Ubica la fila por la misma clave de agrupacion del Historial (D + E + B =
   * nombre + telefono + fecha del referidor). El monto siempre vive en UNA sola
   * fila por programa, para no duplicar al sumar.
   */
  async setVentaMonto(
    group: HistoryGroup,
    vendorId: string,
    vendorName: string,
    monto: number
  ): Promise<void> {
    const accessToken = await tokenManager.ensureValidToken();
    if (!accessToken) {
      throw new Error('No hay token de acceso válido');
    }

    const spreadsheetId = await this.getOrCreateVendorSheet(vendorId, vendorName);

    // Leer B..E para ubicar la primera fila del programa.
    const range = "'Hoja 1'!A2:E";
    const response = await fetch(
      `${SHEETS_API_BASE}/${spreadsheetId}/values/${encodeURIComponent(range)}`,
      { headers: { Authorization: `Bearer ${accessToken}` } }
    );

    if (!response.ok) {
      throw new Error('No se pudo leer la hoja para ubicar el programa');
    }

    const data = await response.json();
    const rows: unknown[][] = data.values || [];

    const targetTel = this.cleanPhone(group.referidor_telefono);
    let targetRow = -1;
    for (let i = 0; i < rows.length; i++) {
      const r = rows[i];
      const fecha = String(r[1] ?? '').trim();          // B
      const nombre = String(r[3] ?? '').trim();          // D
      const tel = this.cleanPhone(String(r[4] ?? ''));   // E
      if (nombre === group.referidor_nombre && tel === targetTel && fecha === group.fecha) {
        targetRow = i + 2; // +2 porque la lectura empieza en la fila 2
        break;
      }
    }

    if (targetRow === -1) {
      throw new Error('No se encontró el programa en la hoja');
    }

    const writeRange = `'Hoja 1'!A${targetRow}`;
    const writeResp = await fetch(
      `${SHEETS_API_BASE}/${spreadsheetId}/values/${encodeURIComponent(writeRange)}?valueInputOption=USER_ENTERED`,
      {
        method: 'PUT',
        headers: {
          Authorization: `Bearer ${accessToken}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ values: [[monto > 0 ? monto : '']] }),
      }
    );

    if (!writeResp.ok) {
      const e = await writeResp.json().catch(() => ({}));
      throw new Error(e.error?.message || 'No se pudo guardar el monto de la venta');
    }
  }

  /**
   * Write rows to the next available position using explicit range (PUT).
   *
   * El bloque que escribe la app va de A a S: el monto de venta (A, solo en la
   * primera fila del programa), los datos del referido (B:Q), el separador R
   * (vacio) y la columna S = "pendiente" (estado inicial de la cita, para que
   * el bot/TMK sepan que falta llamarlo). Las columnas del TMK (T en adelante)
   * NO se tocan.
   *
   * Despues, de forma adicional, se escribe un ID unico por fila en la columna
   * AA (para que el futuro bot de voz ubique cada fila con certeza). Ese paso es
   * "best-effort": si falla, la captura YA quedo guardada y solo se registra una
   * advertencia.
   */
  private async writeRows(
    spreadsheetId: string,
    accessToken: string,
    rows: (string | number)[][]
  ): Promise<void> {
    const nextRow = await this.getNextEmptyRow(spreadsheetId, accessToken);

    const endRow = nextRow + rows.length - 1;
    const range = `'Hoja 1'!A${nextRow}:S${endRow}`;

    console.log('[Sheets] Writing', rows.length, 'rows at', range, 'in spreadsheet:', spreadsheetId);

    const response = await fetch(
      `${SHEETS_API_BASE}/${spreadsheetId}/values/${encodeURIComponent(range)}?valueInputOption=USER_ENTERED`,
      {
        method: 'PUT',
        headers: {
          Authorization: `Bearer ${accessToken}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ values: rows }),
      }
    );

    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      console.error('[Sheets] Write error:', error);
      throw new Error(error.error?.message || 'Error al guardar en Google Sheets');
    }

    console.log('[Sheets] Data written successfully');

    // --- ID unico por fila en la columna AA (para el futuro bot de voz) ---
    // Se escribe en su propia columna, sin tocar las columnas del TMK.
    // Si falla, NO rompemos la captura: los datos ya se guardaron.
    try {
      const idRange = `'Hoja 1'!AA${nextRow}:AA${endRow}`;
      const idValues = rows.map(() => [this.generateId()]);

      const idResponse = await fetch(
        `${SHEETS_API_BASE}/${spreadsheetId}/values/${encodeURIComponent(idRange)}?valueInputOption=USER_ENTERED`,
        {
          method: 'PUT',
          headers: {
            Authorization: `Bearer ${accessToken}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ values: idValues }),
        }
      );

      if (!idResponse.ok) {
        console.warn('[Sheets] No se pudo escribir el ID unico (columna AA). La captura SI quedo guardada.');
      }
    } catch (e) {
      console.warn('[Sheets] Error al escribir el ID unico (no critico):', e);
    }
  }

  /**
   * Genera un identificador unico para cada fila/referido.
   * Usa crypto.randomUUID() cuando esta disponible (navegadores modernos en
   * HTTPS) y, si no, cae a un ID basado en tiempo + aleatorio.
   */
  private generateId(): string {
    const c = (typeof crypto !== 'undefined' ? crypto : undefined) as
      | (Crypto & { randomUUID?: () => string })
      | undefined;
    if (c && typeof c.randomUUID === 'function') {
      return c.randomUUID();
    }
    return `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
  }

  /**
   * Get the next empty row by reading column B (Fecha).
   */
  private async getNextEmptyRow(spreadsheetId: string, accessToken: string): Promise<number> {
    const response = await fetch(
      `${SHEETS_API_BASE}/${spreadsheetId}/values/'Hoja 1'!B:B`,
      {
        headers: { Authorization: `Bearer ${accessToken}` },
      }
    );

    if (!response.ok) {
      return 2;
    }

    const data = await response.json();
    const values = data.values || [];
    return values.length + 1;
  }

  /**
   * Check for duplicate phones in the sheet
   */
  async checkDuplicates(vendorId: string, phones: string[]): Promise<string[]> {
    const settings = await indexedDBService.getSettings(vendorId);
    if (!settings?.spreadsheetId) {
      return [];
    }

    const accessToken = await tokenManager.ensureValidToken();
    if (!accessToken) {
      return [];
    }

    try {
      const response = await fetch(
        `${SHEETS_API_BASE}/${settings.spreadsheetId}/values/K:K`,
        {
          headers: { Authorization: `Bearer ${accessToken}` },
        }
      );

      if (!response.ok) {
        return [];
      }

      const data = await response.json();
      const existingPhones: string[] = (data.values || [])
        .flat()
        .map((phone: string) => this.normalizePhone(phone))
        .filter(Boolean);

      const normalizedInput = phones.map((p) => this.normalizePhone(p));
      return normalizedInput.filter((phone) => existingPhones.includes(phone));
    } catch (error) {
      console.error('Error checking duplicates:', error);
      return [];
    }
  }

  /**
   * Check whether any of the given phones is already registered in the vendor's
   * shared Google Sheet. Looks at BOTH referido phones (col K) and referidor
   * phones (col E), so it catches a number registered under any name.
   *
   * Returns one entry per input phone that already exists, including a short
   * description of who it's registered under. Empty array = no duplicates.
   * Never throws — on any failure it just returns [] (no false alarms).
   */
  async checkDuplicatePhones(
    vendorId: string,
    vendorName: string,
    phones: string[]
  ): Promise<DuplicatePhoneInfo[]> {
    // Clean inputs and drop empty / too-short ones
    const cleanInputs = phones
      .map((p) => ({ original: p, key: this.cleanPhone(p) }))
      .filter((p) => p.key.length >= 7);

    if (cleanInputs.length === 0) {
      return [];
    }

    const accessToken = await tokenManager.ensureValidToken();
    if (!accessToken) {
      return [];
    }

    let spreadsheetId: string;
    try {
      spreadsheetId = await this.getOrCreateVendorSheet(vendorId, vendorName);
    } catch (error) {
      console.error('[Sheets] checkDuplicatePhones: no sheet', error);
      return [];
    }

    try {
      const range = "'Hoja 1'!A2:Q";
      const response = await fetch(
        `${SHEETS_API_BASE}/${spreadsheetId}/values/${encodeURIComponent(range)}`,
        { headers: { Authorization: `Bearer ${accessToken}` } }
      );

      if (!response.ok) {
        return [];
      }

      const data = await response.json();
      const rows: unknown[][] = data.values || [];

      // Map of phone -> description of who owns it
      const registered = new Map<string, string>();

      for (const row of rows) {
        const referidorNombre = String(row[3] ?? '').trim();          // D
        const referidorTel = this.cleanPhone(String(row[4] ?? ''));    // E
        const refNombre = String(row[8] ?? '').trim();                 // I
        const refApellido = String(row[9] ?? '').trim();              // J
        const refTel = this.cleanPhone(String(row[10] ?? ''));         // K

        if (refTel.length >= 7 && !registered.has(refTel)) {
          const fullName = `${refNombre} ${refApellido}`.trim() || 'un referido';
          const desc = referidorNombre
            ? `${fullName} (referido de ${referidorNombre})`
            : fullName;
          registered.set(refTel, desc);
        }

        if (referidorTel.length >= 7 && !registered.has(referidorTel)) {
          registered.set(referidorTel, `${referidorNombre || 'un referidor'} (referidor)`);
        }
      }

      const duplicates: DuplicatePhoneInfo[] = [];
      const seen = new Set<string>();

      for (const input of cleanInputs) {
        if (seen.has(input.key)) continue;
        const owner = registered.get(input.key);
        if (owner) {
          duplicates.push({ phone: input.original, nombre: owner });
          seen.add(input.key);
        }
      }

      return duplicates;
    } catch (error) {
      console.error('Error checking duplicate phones:', error);
      return [];
    }
  }

  /**
   * Read the full capture history from the vendor's Google Sheet,
   * grouped by referidor. Works across all devices (the Sheet is shared).
   *
   * Lee hasta la columna U para traer tambien el resultado del TMK:
   *   S (cita), T (resultado/venta) y U (monto).
   * Tambien lee la columna A (monto de la venta al cliente / referidor).
   */
  async getHistoryFromSheet(vendorId: string, vendorName: string): Promise<HistoryGroup[]> {
    const accessToken = await tokenManager.ensureValidToken();
    if (!accessToken) {
      throw new Error('No hay token de acceso válido');
    }

    const spreadsheetId = await this.getOrCreateVendorSheet(vendorId, vendorName);

    const range = "'Hoja 1'!A2:U";
    const response = await fetch(
      `${SHEETS_API_BASE}/${spreadsheetId}/values/${encodeURIComponent(range)}`,
      { headers: { Authorization: `Bearer ${accessToken}` } }
    );

    if (!response.ok) {
      throw new Error('Error al leer el historial del Sheet');
    }

    const data = await response.json();
    const rows: unknown[][] = data.values || [];

    const groupsMap = new Map<string, HistoryGroup>();

    for (const row of rows) {
      const ventaMonto = this.parseMoney(String(row[0] ?? ''));  // A: Venta $ (cliente)
      const fecha = String(row[1] ?? '').trim();             // B: Fecha
      const referidorNombre = String(row[3] ?? '').trim();   // D: Cliente
      const referidorTel = String(row[4] ?? '').trim();      // E: Teléfono
      const regaloEntregado = String(row[6] ?? '').trim();   // G: Regalo entregado
      const premioSiCompran = String(row[7] ?? '').trim();   // H: Premio si compran
      const refNombre = String(row[8] ?? '').trim();         // I: Nombre
      const refApellido = String(row[9] ?? '').trim();       // J: Apellido
      const refTel = String(row[10] ?? '').trim();           // K: Teléfono
      const refCiudad = String(row[11] ?? '').trim();        // L: Ciudad
      const refRelacion = String(row[12] ?? '').trim();      // M: Relación
      const refTrabaja = String(row[13] ?? '').trim();       // N: Trabaja
      const refDato = String(row[14] ?? '').trim();          // O: Dato Extra
      const refCita = String(row[18] ?? '').trim();          // S: Cita (estado TMK/bot)
      const refResultado = String(row[19] ?? '').trim();     // T: Resultado / Venta
      const refMonto = String(row[20] ?? '').trim();         // U: Monto de la venta

      // Combine the immediate gift (G) and the conditional prize (H) for display.
      let regalo = regaloEntregado;
      if (premioSiCompran) {
        regalo = regaloEntregado
          ? `${regaloEntregado} · Premio: ${premioSiCompran}`
          : `Premio: ${premioSiCompran}`;
      }

      if (!referidorNombre && !refNombre) continue;

      const key = `${referidorNombre}__${referidorTel}__${fecha}`;

      if (!groupsMap.has(key)) {
        groupsMap.set(key, {
          id: key,
          fecha,
          fechaOrden: this.parseSheetDate(fecha),
          referidor_nombre: referidorNombre,
          referidor_telefono: referidorTel,
          regalo,
          venta_monto: 0,
          referidos: [],
        });
      }

      const group = groupsMap.get(key)!;

      // El monto de venta vive en una sola fila del programa; al sumar todas las
      // filas del grupo obtenemos ese monto (las demas traen 0).
      group.venta_monto += ventaMonto;

      if (refNombre || refTel) {
        group.referidos.push({
          id: `${key}__${group.referidos.length}`,
          nombre: refNombre,
          apellido: refApellido,
          telefono: refTel,
          ciudad: refCiudad,
          relacion: refRelacion,
          trabaja: refTrabaja,
          dato_extra: refDato,
          cita: refCita,
          resultado: refResultado,
          monto: refMonto,
        });
      }
    }

    const groups = Array.from(groupsMap.values());
    groups.sort((a, b) => b.fechaOrden - a.fechaOrden);
    return groups;
  }

  /**
   * Read counts (today / week / month) and the spreadsheet id for the dashboard.
   * Reads from the shared Google Sheet so all devices show the same numbers.
   */
  async getDashboardData(
    vendorId: string,
    vendorName: string
  ): Promise<{ today: number; week: number; month: number; spreadsheetId: string }> {
    const accessToken = await tokenManager.ensureValidToken();
    if (!accessToken) {
      throw new Error('No hay token de acceso válido');
    }

    const spreadsheetId = await this.getOrCreateVendorSheet(vendorId, vendorName);

    const range = "'Hoja 1'!A2:Q";
    const response = await fetch(
      `${SHEETS_API_BASE}/${spreadsheetId}/values/${encodeURIComponent(range)}`,
      { headers: { Authorization: `Bearer ${accessToken}` } }
    );

    if (!response.ok) {
      throw new Error('Error al leer los datos del Sheet');
    }

    const data = await response.json();
    const rows: unknown[][] = data.values || [];

    const now = new Date();
    const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
    const startOfWeek = startOfToday - 6 * 24 * 60 * 60 * 1000;
    const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1).getTime();

    let today = 0;
    let week = 0;
    let month = 0;

    for (const row of rows) {
      const fecha = String(row[1] ?? '').trim();
      const refNombre = String(row[8] ?? '').trim();
      const refTel = String(row[10] ?? '').trim();

      if (!refNombre && !refTel) continue;

      const t = this.parseSheetDate(fecha);
      if (t >= startOfToday) today++;
      if (t >= startOfWeek) week++;
      if (t >= startOfMonth) month++;
    }

    return { today, week, month, spreadsheetId };
  }

  /**
   * Rich dashboard stats from the shared Google Sheet, per period
   * (hoy / semana / mes):
   *   - referidos: total de contactos capturados
   *   - completos: referidores con 20+ contactos (recibieron regalo)
   *   - incompletos: referidores con 1-19 contactos
   *   - citas: contactos marcados como Cita Instantanea EN LA RESPUESTA (columna Q)
   *   - ventas: programas con monto de venta al cliente (columna A)
   *   - montoVentas: suma de esos montos ($)
   * Programas se agrupan por referidor (nombre + telefono + fecha) y se
   * cuentan por la fecha del referidor.
   */
  async getDashboardStats(vendorId: string, vendorName: string): Promise<DashboardStats> {
    const accessToken = await tokenManager.ensureValidToken();
    if (!accessToken) {
      throw new Error('No hay token de acceso válido');
    }

    const spreadsheetId = await this.getOrCreateVendorSheet(vendorId, vendorName);

    const range = "'Hoja 1'!A2:Q";
    const response = await fetch(
      `${SHEETS_API_BASE}/${spreadsheetId}/values/${encodeURIComponent(range)}`,
      { headers: { Authorization: `Bearer ${accessToken}` } }
    );

    if (!response.ok) {
      throw new Error('Error al leer los datos del Sheet');
    }

    const data = await response.json();
    const rows: unknown[][] = data.values || [];

    const now = new Date();
    const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
    const startOfWeek = startOfToday - 6 * 24 * 60 * 60 * 1000;
    const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1).getTime();

    const empty = (): PeriodCounts => ({ today: 0, week: 0, month: 0 });
    const referidos = empty();
    const completos = empty();
    const incompletos = empty();
    const citas = empty();
    const ventas = empty();
    const montoVentas = empty();

    const addPeriod = (counts: PeriodCounts, t: number) => {
      if (t >= startOfToday) counts.today++;
      if (t >= startOfWeek) counts.week++;
      if (t >= startOfMonth) counts.month++;
    };

    const addMoney = (counts: PeriodCounts, t: number, amount: number) => {
      if (t >= startOfToday) counts.today += amount;
      if (t >= startOfWeek) counts.week += amount;
      if (t >= startOfMonth) counts.month += amount;
    };

    // Group rows by referidor (name + phone + date) to measure program size + sale.
    const groups = new Map<string, { date: number; count: number; monto: number }>();

    for (const row of rows) {
      const fecha = String(row[1] ?? '').trim();              // B: Fecha
      const t = this.parseSheetDate(fecha);
      const ventaMonto = this.parseMoney(String(row[0] ?? '')); // A: Venta $ (cliente)
      const referidorNombre = String(row[3] ?? '').trim();     // D
      const referidorTel = String(row[4] ?? '').trim();        // E
      const refNombre = String(row[8] ?? '').trim();           // I
      const refTel = String(row[10] ?? '').trim();             // K
      const prioridad = String(row[16] ?? '').trim();          // Q: Prioridad (respuesta del referido)

      const hasReferido = !!(refNombre || refTel);

      if (hasReferido) {
        addPeriod(referidos, t);
        // La cita instantanea ahora se cuenta desde la RESPUESTA (columna Q),
        // que es cuando de verdad se sabe si hubo cita.
        if (prioridad === 'CITA_INSTANTANEA') {
          addPeriod(citas, t);
        }
      }

      if (referidorNombre || referidorTel) {
        const key = `${referidorNombre}__${referidorTel}__${fecha}`;
        const g = groups.get(key) || { date: t, count: 0, monto: 0 };
        if (hasReferido) g.count++;
        g.monto += ventaMonto; // el monto vive en una sola fila del programa
        groups.set(key, g);
      }
    }

    for (const g of groups.values()) {
      if (g.count >= 20) {
        addPeriod(completos, g.date);
      } else if (g.count >= 1) {
        addPeriod(incompletos, g.date);
      }

      if (g.monto > 0) {
        addPeriod(ventas, g.date);
        addMoney(montoVentas, g.date, g.monto);
      }
    }

    return { referidos, completos, incompletos, citas, ventas, montoVentas, spreadsheetId };
  }

  private parseSheetDate(fecha: string): number {
    const parts = fecha.split('/');
    if (parts.length === 3) {
      const d = parseInt(parts[0], 10);
      const m = parseInt(parts[1], 10);
      const y = parseInt(parts[2], 10);
      if (!isNaN(d) && !isNaN(m) && !isNaN(y)) {
        return new Date(y, m - 1, d).getTime();
      }
    }
    const t = new Date(fecha).getTime();
    return isNaN(t) ? 0 : t;
  }

  /**
   * Convierte el texto de un monto (ej. "$1,899.00", "1899") a numero.
   * Quita todo lo que no sea digito o punto. Si no hay nada valido, 0.
   */
  private parseMoney(raw: string): number {
    if (!raw) return 0;
    const cleaned = String(raw).replace(/[^0-9.]/g, '');
    if (!cleaned) return 0;
    const n = parseFloat(cleaned);
    return isNaN(n) ? 0 : n;
  }

  /**
   * Column layout:
   * A(0):Venta $ (monto de venta al cliente / referidor)
   * B(1):Fecha  C(2):Asesor  D(3):Cliente  E(4):Teléfono  F(5):Dirección
   * G(6):Regalo entregado  H(7):Premio si compran  I(8):Nombre  J(9):Apellido  K(10):Teléfono
   * L(11):Ciudad  M(12):Relación  N(13):Trabaja  O(14):Dato Extra
   * P(15):SMS (medio de envío)  Q(16):Prioridad (respuesta: OK_GRACIAS / CITA_INSTANTANEA)
   * --- columnas del TMK / bot ---
   * R(17):separador  S(18):Cita  T(19):Resultado/Venta  U(20):Monto
   * V-Z(21-25):TMK1-5 (registro de llamadas)
   * --- columna de la app ---
   * AA(26):ID unico (lo escribe la app al guardar, para el futuro bot de voz)
   *
   * La app escribe A (monto venta, solo primera fila del programa), B:Q (datos),
   * R vacio, y S = "pendiente" (estado inicial de la cita). El resto de columnas
   * del TMK (T en adelante) las llena el TMK/bot.
   */
  private readonly NUM_COLS = 17; // A through Q (bloque de datos del referido)

  /**
   * Column Q (Prioridad): the referido's RESPONSE, but only the two that matter
   * to the TMK — OK_GRACIAS (priorizar llamada) and CITA_INSTANTANEA (NO marcar,
   * ya hubo cita). El resto de respuestas queda en blanco.
   */
  private prioridadValue(referido: Referido): string {
    if (
      referido.respuesta_referido === 'OK_GRACIAS' ||
      referido.respuesta_referido === 'CITA_INSTANTANEA'
    ) {
      return referido.respuesta_referido;
    }
    return '';
  }

  /**
   * Columna P (medio de envío). Internamente la "Llamada instantánea" usa el
   * código 'CITA_INSTANTANEA', pero en la hoja lo escribimos como
   * 'LLAMADA_INSTANTANEA' para que no se confunda con una cita real (que vive
   * en la columna Q).
   */
  private medioValue(referido: Referido): string {
    if (referido.mensaje_enviado === 'CITA_INSTANTANEA') {
      return 'LLAMADA_INSTANTANEA';
    }
    return referido.mensaje_enviado || '';
  }

  /**
   * Columna S (Cita): estado inicial con el que se guarda un referido nuevo.
   * Si su respuesta fue CITA_INSTANTANEA (ya hubo cita en el campo), nace como
   * "AGENDO" (verde) para que el bot/TMK no lo llamen de mas. Si no, "pendiente".
   * Usa el mismo campo que la columna Q, asi S y Q siempre van coordinados.
   */
  private citaInicial(referido: Referido): string {
    if (referido.respuesta_referido === 'CITA_INSTANTANEA') {
      return CITA_YA_AGENDADA;
    }
    return CITA_INICIAL;
  }

  private mapToSheetRows(referidor: Referidor, referidos: Referido[]): (string | number)[][] {
    if (referidos.length === 0) {
      return [this.mapReferidorOnlyRow(referidor)];
    }

    return referidos.map((referido, idx) => {
      const row = new Array(this.NUM_COLS).fill('');

      // A: Monto de la venta al cliente. SOLO en la primera fila del programa,
      // para no duplicar al sumar (los datos del referidor se repiten por fila).
      if (idx === 0 && referidor.monto_venta && referidor.monto_venta > 0) {
        row[0] = referidor.monto_venta;
      }

      row[1] = this.formatDate(referidor.fecha_registro);  // B: Fecha
      row[2] = referidor.vendedor_nombre;                   // C: Asesor
      row[3] = referidor.nombre_completo;                   // D: Cliente
      row[4] = this.cleanPhone(referidor.telefono);         // E: Teléfono
      row[5] = referidor.direccion || '';                    // F: Dirección
      row[6] = referidor.regalo_categoria ? GIFT_MAP[referidor.regalo_categoria] : ''; // G: Regalo entregado
      row[7] = referidor.regalo_detalle || '';               // H: Premio si compran

      row[8] = referido.nombre;                             // I: Nombre
      row[9] = referido.apellido || '';                     // J: Apellido (optional)
      row[10] = this.cleanPhone(referido.telefono);          // K: Teléfono
      row[11] = referido.ciudad;                             // L: Ciudad
      row[12] = referido.relacion;                           // M: Relación
      row[13] = referido.trabaja;                            // N: Trabaja
      row[14] = referido.dato_extra || '';                   // O: Dato Extra
      row[15] = this.medioValue(referido);                   // P: medio de envío

      row[16] = this.prioridadValue(referido);               // Q: Prioridad (OK_GRACIAS / CITA_INSTANTANEA)

      row[17] = '';                                          // R: separador (vacio)
      row[18] = this.citaInicial(referido);                  // S: Cita inicial (pendiente, o AGENDO si ya hubo cita instantanea)

      return row;
    });
  }

  private mapReferidorOnlyRow(referidor: Referidor): (string | number)[] {
    const row = new Array(this.NUM_COLS).fill('');

    if (referidor.monto_venta && referidor.monto_venta > 0) {
      row[0] = referidor.monto_venta;                       // A: Monto de venta al cliente
    }

    row[1] = this.formatDate(referidor.fecha_registro);  // B: Fecha
    row[2] = referidor.vendedor_nombre;                   // C: Asesor
    row[3] = referidor.nombre_completo;                   // D: Cliente
    row[4] = this.cleanPhone(referidor.telefono);         // E: Teléfono
    row[5] = referidor.direccion || '';                    // F: Dirección
    row[6] = referidor.regalo_categoria ? GIFT_MAP[referidor.regalo_categoria] : ''; // G: Regalo entregado
    row[7] = referidor.regalo_detalle || '';               // H: Premio si compran

    row[17] = '';  // R: separador (vacio)
    row[18] = '';  // S: sin "pendiente" (no hay referido al cual llamar)

    return row;
  }

  private mapReferidoToRow(referido: Referido): (string | number)[] {
    const row = new Array(this.NUM_COLS).fill('');

    row[8] = referido.nombre;                    // I: Nombre
    row[9] = referido.apellido || '';             // J: Apellido (optional)
    row[10] = this.cleanPhone(referido.telefono);  // K: Teléfono
    row[11] = referido.ciudad;                     // L: Ciudad
    row[12] = referido.relacion;                   // M: Relación
    row[13] = referido.trabaja;                    // N: Trabaja
    row[14] = referido.dato_extra || '';            // O: Dato Extra
    row[15] = this.medioValue(referido);            // P: medio de envío
    row[16] = this.prioridadValue(referido);        // Q: Prioridad (OK_GRACIAS / CITA_INSTANTANEA)

    row[17] = '';            // R: separador (vacio)
    row[18] = this.citaInicial(referido);  // S: Cita inicial (pendiente, o AGENDO si ya hubo cita instantanea)

    return row;
  }

  /**
   * Fila para un referido que se AGREGA a un programa ya existente.
   * Repite los datos del programa (B, C, D, E) para que la fila agrupe junto al
   * programa original en el Historial (clave de agrupacion = D + E + B), y llena
   * los datos del nuevo referido (I..S). A, F, G, H van vacias a proposito.
   *
   * El telefono del referidor se vuelve a pasar por cleanPhone para que quede
   * EXACTAMENTE igual que en las filas originales (mismo valor almacenado en E)
   * y la clave de agrupacion coincida.
   */
  private mapReferidoToProgramRow(
    group: HistoryGroup,
    asesorNombre: string,
    referido: Referido
  ): (string | number)[] {
    const row = new Array(this.NUM_COLS).fill('');

    // --- Datos del programa original (clave de agrupacion: D + E + B) ---
    row[1] = group.fecha;                                 // B: Fecha (la del programa)
    row[2] = asesorNombre || '';                          // C: Asesor
    row[3] = group.referidor_nombre;                      // D: Cliente / referidor
    row[4] = this.cleanPhone(group.referidor_telefono);   // E: Tel referidor
    // A, F, G, H: vacias a proposito (el grupo ya las tiene de sus filas originales)

    // --- Datos del nuevo referido ---
    row[8] = referido.nombre;                             // I: Nombre
    row[9] = referido.apellido || '';                     // J: Apellido
    row[10] = this.cleanPhone(referido.telefono);          // K: Teléfono
    row[11] = referido.ciudad;                             // L: Ciudad
    row[12] = referido.relacion;                           // M: Relación
    row[13] = referido.trabaja;                            // N: Trabaja
    row[14] = referido.dato_extra || '';                   // O: Dato Extra
    row[15] = this.medioValue(referido);                   // P: medio de envío
    row[16] = this.prioridadValue(referido);               // Q: Prioridad

    row[17] = '';                                          // R: separador (vacio)
    row[18] = this.citaInicial(referido);                  // S: Cita inicial (pendiente, o AGENDO)

    return row;
  }

  private formatDate(isoDate: string): string {
    const date = new Date(isoDate);
    return date.toLocaleDateString('es-MX', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    });
  }

  private cleanPhone(phone: string): string {
    let digits = this.normalizePhone(phone);
    if (digits.length === 11 && digits.startsWith('1')) {
      digits = digits.slice(1);
    }
    return digits;
  }

  private normalizePhone(phone: string): string {
    return phone.replace(/\D/g, '');
  }
}

export const sheetsService = new SheetsService();
